import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit2, Play, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useDeal, useUpdateDeal } from '@/lib/hooks';
import { formatCurrency, formatPercentage, cn } from '@/lib/utils';
import {
  transactionStructureLabels,
  transactionStructureIcons,
  transactionStructureDescriptions,
  stageLabels,
  stageColors,
  temperatureLabels,
  temperatureColors,
  gpConsentStatusLabels,
  gpConsentStatusColors,
  DealStage,
  GPConsentStatus,
  type TransactionStructureType,
  type DealStageType,
  type GPConsentStatusType,
} from '@/types/database';
import { NegotiationPanel } from '@/components/negotiation';

const stageOrder: DealStageType[] = [
  DealStage.SOURCED,
  DealStage.LOI_SENT,
  DealStage.DUE_DILIGENCE,
  DealStage.GP_CONSENT,
  DealStage.DOCUMENTATION,
  DealStage.CLOSING,
  DealStage.CLOSED,
];

export function DealDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState<DealStageType | null>(null);
  const [editingProbability, setEditingProbability] = useState(false);
  const [editingCloseDate, setEditingCloseDate] = useState(false);
  const [probabilityValue, setProbabilityValue] = useState<string>('50');
  const [closeDateValue, setCloseDateValue] = useState<string>('');

  const { data: deal, isLoading, error } = useDeal(id);
  const updateDeal = useUpdateDeal();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="text-center py-12">
        <p className="text-negative font-medium mb-4">Failed to load deal</p>
        <Link to="/deals" className="text-primary hover:text-primary-hover font-medium">
          Back to Deals
        </Link>
      </div>
    );
  }

  const currentStageIndex = stageOrder.indexOf(deal.stage);
  const gpConsentStatus = (deal as unknown as { gp_consent_status?: GPConsentStatusType }).gp_consent_status ?? GPConsentStatus.PENDING;

  // Initialize probability and close date when deal loads
  const closingProb = deal.closing_probability ?? 50;
  const estimatedClose = deal.estimated_close_date ?? '';

  const handleProbabilityUpdate = async () => {
    if (!id) return;
    const prob = parseInt(probabilityValue);
    if (isNaN(prob) || prob < 0 || prob > 100) return;

    await updateDeal.mutateAsync({
      id,
      data: { closing_probability: prob },
    });
    setEditingProbability(false);
  };

  const handleCloseDateUpdate = async () => {
    if (!id) return;

    // Only include the field if there's a value
    const data: { estimated_close_date?: string } = {};
    if (closeDateValue) {
      data.estimated_close_date = closeDateValue;
    }

    await updateDeal.mutateAsync({ id, data });
    setEditingCloseDate(false);
  };

  const handleStageUpdate = async () => {
    if (!selectedStage || !id) return;

    await updateDeal.mutateAsync({
      id,
      data: { stage: selectedStage },
    });

    setShowStageModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Link
            to="/deals"
            className="mt-1 p-2 text-text-tertiary hover:text-text-primary hover:bg-surface-hover rounded-md transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{transactionStructureIcons[deal.transaction_structure as TransactionStructureType]}</span>
              <h2 className="text-2xl font-bold text-text-primary">{deal.name}</h2>
              <span className={cn(
                'px-2 py-1 text-xs font-semibold rounded-full border',
                temperatureColors[deal.temperature as keyof typeof temperatureColors]
              )}>
                {temperatureLabels[deal.temperature as keyof typeof temperatureLabels]}
              </span>
            </div>
            <p className="text-text-secondary mt-1">
              {transactionStructureLabels[deal.transaction_structure as TransactionStructureType]}
              {deal.partial_percentage && ` (${(deal.partial_percentage * 100).toFixed(0)}%)`}
              {deal.fund_name && ` • ${deal.fund_name}`}
              {deal.gp_name && ` • ${deal.gp_name}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-4 py-2 border border-border text-text-secondary rounded-md hover:bg-surface-hover transition-colors flex items-center gap-2 shadow-sm font-medium">
            <Edit2 className="h-4 w-4" />
            Edit
          </button>
          <Link
            to="/workflows"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors flex items-center gap-2 shadow-sm font-medium"
          >
            <Play className="h-4 w-4" />
            Run Workflow
          </Link>
        </div>
      </div>

      {/* Stage Progress */}
      <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Deal Stage</h3>
          <button
            onClick={() => {
              setSelectedStage(deal.stage);
              setShowStageModal(true);
            }}
            className="text-sm text-primary hover:text-primary-hover font-medium"
          >
            Update Stage
          </button>
        </div>
        <div className="flex items-center gap-2">
          {stageOrder.slice(0, -1).map((stage, index) => {
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            return (
              <div key={stage} className="flex items-center flex-1">
                <div className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors',
                  isCompleted && 'bg-positive border-positive text-white',
                  isCurrent && 'bg-primary border-primary text-white',
                  !isCompleted && !isCurrent && 'bg-muted border-border text-text-tertiary'
                )}>
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <span className="text-xs font-semibold">{index + 1}</span>
                  )}
                </div>
                <div className={cn(
                  'flex-1 h-1 mx-2 transition-colors',
                  isCompleted ? 'bg-positive' : 'bg-border'
                )} />
              </div>
            );
          })}
          <div className={cn(
            'flex items-center justify-center w-8 h-8 rounded-full border-2',
            deal.stage === DealStage.CLOSED ? 'bg-positive border-positive text-white' : 'bg-muted border-border text-text-tertiary'
          )}>
            <CheckCircle className="h-5 w-5" />
          </div>
        </div>
        <div className="flex justify-between mt-2">
          {stageOrder.slice(0, -1).map((stage) => (
            <span key={stage} className="text-xs text-text-tertiary flex-1 text-center">
              {stageLabels[stage]}
            </span>
          ))}
          <span className="text-xs text-text-tertiary w-8 text-center">Closed</span>
        </div>
      </div>

      {/* Negotiation Automation Panel */}
      <NegotiationPanel deal={deal} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Key Metrics */}
          <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Key Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg border border-border">
                <p className="text-sm text-text-secondary font-medium">Target Value</p>
                <p className="text-xl font-bold text-text-primary mt-1">{formatCurrency(deal.target_value)}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg border border-border">
                <p className="text-sm text-text-secondary font-medium">Headline Discount</p>
                <p className="text-xl font-bold text-text-primary mt-1">
                  {deal.headline_discount ? formatPercentage(deal.headline_discount) : '-'}
                </p>
              </div>
              <div className="p-4 bg-accent-subtle rounded-lg border border-accent">
                <p className="text-sm text-accent font-semibold">TRUE Discount</p>
                <p className="text-xl font-bold text-accent mt-1">
                  {deal.true_discount ? formatPercentage(deal.true_discount) : '-'}
                </p>
              </div>
              <div className="p-4 bg-warning-subtle rounded-lg border border-warning">
                <p className="text-sm text-warning font-semibold">Fee Drag</p>
                <p className="text-xl font-bold text-warning mt-1">
                  {deal.fee_drag ? formatPercentage(deal.fee_drag) : '-'}
                </p>
              </div>
            </div>

            {/* Editable Probability and Timeline */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="p-4 bg-positive-subtle rounded-lg border border-positive">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-positive font-semibold">Closing Probability</p>
                  {!editingProbability && (
                    <button
                      onClick={() => {
                        setProbabilityValue(String(closingProb));
                        setEditingProbability(true);
                      }}
                      className="text-xs text-positive hover:text-positive/80"
                    >
                      <Edit2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {editingProbability ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={probabilityValue}
                      onChange={(e) => setProbabilityValue(e.target.value)}
                      className="w-20 px-2 py-1 text-lg font-bold border border-positive rounded bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-positive"
                      autoFocus
                    />
                    <span className="text-lg font-bold text-positive">%</span>
                    <button
                      onClick={handleProbabilityUpdate}
                      disabled={updateDeal.isPending}
                      className="px-2 py-1 bg-positive text-white text-xs rounded hover:bg-positive/90 disabled:opacity-50 font-medium"
                    >
                      {updateDeal.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingProbability(false)}
                      className="px-2 py-1 border border-border text-text-secondary text-xs rounded hover:bg-surface-hover"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-positive">{closingProb}%</p>
                )}
              </div>

              <div className="p-4 bg-primary-subtle rounded-lg border border-primary">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-primary font-semibold">Est. Close Date</p>
                  {!editingCloseDate && (
                    <button
                      onClick={() => {
                        setCloseDateValue(estimatedClose);
                        setEditingCloseDate(true);
                      }}
                      className="text-xs text-primary hover:text-primary-hover"
                    >
                      <Edit2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {editingCloseDate ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={closeDateValue}
                      onChange={(e) => setCloseDateValue(e.target.value)}
                      className="px-2 py-1 text-sm font-bold border border-primary rounded bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                      autoFocus
                    />
                    <button
                      onClick={handleCloseDateUpdate}
                      disabled={updateDeal.isPending}
                      className="px-2 py-1 bg-primary text-white text-xs rounded hover:bg-primary-hover disabled:opacity-50 font-medium"
                    >
                      {updateDeal.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingCloseDate(false)}
                      className="px-2 py-1 border border-border text-text-secondary text-xs rounded hover:bg-surface-hover"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <p className="text-xl font-bold text-primary">
                    {estimatedClose ? new Date(estimatedClose).toLocaleDateString() : 'Not set'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Fund Details */}
          {(deal.fund_name || deal.gp_name) && (
            <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Fund Details</h3>
              <div className="grid grid-cols-2 gap-4">
                {deal.fund_name && (
                  <div>
                    <p className="text-sm text-text-secondary font-medium">Fund Name</p>
                    <p className="font-semibold text-text-primary mt-1">{deal.fund_name}</p>
                  </div>
                )}
                {deal.gp_name && (
                  <div>
                    <p className="text-sm text-text-secondary font-medium">GP</p>
                    <p className="font-semibold text-text-primary mt-1">{deal.gp_name}</p>
                  </div>
                )}
                {deal.vintage_year && (
                  <div>
                    <p className="text-sm text-text-secondary font-medium">Vintage Year</p>
                    <p className="font-semibold text-text-primary mt-1">{deal.vintage_year}</p>
                  </div>
                )}
                {deal.fund_size && (
                  <div>
                    <p className="text-sm text-text-secondary font-medium">Fund Size</p>
                    <p className="font-semibold text-text-primary mt-1">{formatCurrency(deal.fund_size)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Seller Information */}
          <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Seller Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-secondary font-medium">Seller Name</p>
                <p className="font-semibold text-text-primary mt-1">{deal.seller_name}</p>
              </div>
              <div>
                <p className="text-sm text-text-secondary font-medium">Contact Email</p>
                <p className="font-semibold text-text-primary mt-1">{deal.seller_email ?? '-'}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {deal.notes && (
            <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Notes</h3>
              <p className="text-text-primary whitespace-pre-wrap">{deal.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Transaction Structure */}
          <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Transaction Structure</h3>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">{transactionStructureIcons[deal.transaction_structure as TransactionStructureType]}</span>
              <div>
                <p className="font-semibold text-text-primary">
                  {transactionStructureLabels[deal.transaction_structure as TransactionStructureType]}
                </p>
                {deal.partial_percentage && (
                  <p className="text-sm text-text-secondary font-medium mt-1">
                    {(deal.partial_percentage * 100).toFixed(0)}% of position
                  </p>
                )}
              </div>
            </div>
            <p className="text-sm text-text-secondary">
              {transactionStructureDescriptions[deal.transaction_structure as TransactionStructureType]}
            </p>
          </div>

          {/* GP Consent Status */}
          <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-text-primary mb-4">GP Consent</h3>
            <div className="flex items-center gap-2 mb-3">
              <span className={cn('w-3 h-3 rounded-full', {
                'bg-text-tertiary': gpConsentStatus === 'pending',
                'bg-primary': gpConsentStatus === 'submitted',
                'bg-positive': gpConsentStatus === 'approved',
                'bg-warning': gpConsentStatus === 'rofr_active' || gpConsentStatus === 'rofr_exercised',
                'bg-negative': gpConsentStatus === 'denied',
              })} />
              <span className={cn('font-semibold', gpConsentStatusColors[gpConsentStatus])}>
                {gpConsentStatusLabels[gpConsentStatus]}
              </span>
            </div>
          </div>

          {/* Next Action */}
          {deal.next_action && (
            <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Next Action</h3>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-warning mt-0.5" />
                <p className="text-text-primary">{deal.next_action}</p>
              </div>
            </div>
          )}

          {/* Activity Timeline */}
          <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Activity</h3>
            {(deal.activities?.length ?? 0) === 0 ? (
              <p className="text-text-tertiary text-center py-4">No activity yet</p>
            ) : (
              <div className="space-y-4">
                {deal.activities?.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-border last:border-0">
                    <div className="mt-1">
                      <Clock className="h-4 w-4 text-text-tertiary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-text-primary">{activity.description}</p>
                      <p className="text-xs text-text-tertiary font-mono mt-1">
                        {activity.actor} • {new Date(activity.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stage Update Modal */}
      {showStageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-surface rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-border">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Update Deal Stage</h3>
            <div className="space-y-2">
              {stageOrder.map((stage) => (
                <label
                  key={stage}
                  className={cn(
                    'flex items-center p-3 border rounded-md cursor-pointer transition-colors',
                    selectedStage === stage
                      ? 'border-primary bg-primary-subtle'
                      : 'border-border hover:bg-surface-hover'
                  )}
                >
                  <input
                    type="radio"
                    name="stage"
                    value={stage}
                    checked={selectedStage === stage}
                    onChange={() => setSelectedStage(stage)}
                    className="mr-3"
                  />
                  <span className={cn(
                    'px-2 py-0.5 text-xs font-semibold rounded-full border mr-2',
                    stageColors[stage]
                  )}>
                    {stageLabels[stage]}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowStageModal(false)}
                className="px-4 py-2 border border-border text-text-secondary rounded-md hover:bg-surface-hover transition-colors"
                disabled={updateDeal.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleStageUpdate}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover disabled:opacity-50 flex items-center gap-2 shadow-sm font-medium transition-colors"
                disabled={updateDeal.isPending}
              >
                {updateDeal.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Update Stage
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
